import React, { memo } from 'react';

import { thumbhashToSoftTint } from '../../lib/thumbdata';
import { cn } from '../../lib/utils';

const TRANSPARENT_IMAGE_URL = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8+B8AAucB8kTMRj8AAAAASUVORK5CYII='; // prettier-ignore

export function resolveImageFlexiconUrl(image = {}) {
  // The upload pipeline eventually emits square variants, but fresh flexicon
  // uploads can render from the original object URL immediately after upload.
  return (
    image.variants?.square_medium?.public_url ||
    image.variants?.square_large?.public_url ||
    image.variants?.square_thumb?.public_url ||
    image.variants?.medium?.public_url ||
    image.variants?.thumb?.public_url ||
    image.public_url ||
    null
  );
}

export const getFlexiconSignature = (resource) => {
  const flexicon = resource?.flexicon;
  if (!flexicon) return 'none';

  switch (flexicon.kind) {
    case 'emoji': {
      const emoji = flexicon.emoji || {};
      return ['emoji', emoji.public_url, emoji.native, emoji.shortcode, emoji.thumbhash].join('|');
    }
    case 'image': {
      const image = flexicon.image || {};
      return ['image', resolveImageFlexiconUrl(image), image.file_name].join('|');
    }
    case 'glyph': {
      const glyph = flexicon.glyph || {};
      return ['glyph', glyph.public_url, glyph.kind, glyph.filename].join('|');
    }
    default:
      return flexicon.kind;
  }
};

const FlexiconComponent = ({ resource, size = 'size-6', className = '' }) => {
  // dim the flexicon if the resource is deactivated
  const deactivated = resource?.role === 'deactivated' ? 'brightness-150 contrast-50 grayscale-100' : '';

  const wrapperClassName = cn(
    'relative box-content inline-flex aspect-square flex-shrink-0 overflow-hidden select-none',
    size,
    deactivated,
    'rounded-full',
    className,
  );

  const innerClassName = `absolute inset-0 select-none inline-flex items-center justify-center overflow-hidden ${size}`;

  let contents;
  const flexicon = resource?.flexicon;
  if (flexicon?.kind === 'emoji') {
    const emoji = flexicon?.emoji || {};
    const backgroundColor = thumbhashToSoftTint(emoji?.thumbhash);

    if (emoji?.public_url) {
      contents = (
        <span className={innerClassName} style={{ backgroundColor }}>
          <img
            src={emoji.public_url}
            className="aspect-square w-full scale-60"
            alt={emoji.native || emoji.shortcode || ''}
            draggable="false"
          />
          <img
            src={emoji.public_url}
            className="absolute inset-0 aspect-square w-full scale-60 opacity-30 mix-blend-overlay"
            draggable="false"
          />
        </span>
      );
    }
  } else if (flexicon?.kind === 'image') {
    const image = flexicon.image || {};
    const imageUrl = resolveImageFlexiconUrl(image);
    if (imageUrl) {
      contents = <img src={imageUrl} alt={image?.file_name || ''} className={`${innerClassName} aspect-square object-cover`} draggable="false" />;
    }
  } else if (flexicon?.kind === 'glyph') {
    const glyph = flexicon.glyph || {};
    if (glyph?.public_url) {
      contents = <img src={glyph.public_url} alt={glyph?.filename || ''} className={`${innerClassName} aspect-square`} draggable="false" />;
    }
  }

  // fallback if no resource was provided or the content could not be rendered
  if (!contents) contents = <img src={TRANSPARENT_IMAGE_URL} alt="" className={`${innerClassName} aspect-square`} draggable="false" />;

  const innerShadow = <span className="flexicon-inner-shadow pointer-events-none absolute inset-0 z-1 rounded-full" />;

  return (
    <span className={wrapperClassName}>
      {contents}
      {innerShadow}
    </span>
  );
};

const propsAreEqual = (prevProps, nextProps) => {
  if (prevProps.size !== nextProps.size) return false;
  if (prevProps.className !== nextProps.className) return false;
  if (prevProps.resource?.role !== nextProps.resource?.role) return false;

  return getFlexiconSignature(prevProps.resource) === getFlexiconSignature(nextProps.resource);
};

// Memoize component and skip rerender when propsAreEqual finds no changes
export const Flexicon = memo(FlexiconComponent, propsAreEqual);

export default Flexicon;
